import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import toast from 'react-hot-toast';
import Badge from '../../../components/ui/Badge';
import {
  CheckCircleIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  EyeIcon,
  GeneralList,
  GeneralListSortableTh,
  LockIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  UndoIcon,
  useConfirm,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../../components/ui';
import { TruncatedText } from '../../../components/DataTable/TruncatedText';
import DateFilterDropdown, { DateRange } from '../../../components/FiltersPanel/DateFilterDropdown';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import {
  deleteAccDocument,
  revertAccDocumentToDraft,
  saveAccDocument,
} from '../../../services/mutations';
import { accountingReturnState } from '../shared/navigation';
import {
  AccDocument,
  useAccDocuments,
} from './useData';
import {
  buildYearFilterOptions,
  moduleConfig,
} from './config';
import { resolveDocumentEditRoute } from './formTemplates';

type StatusTab = 'all' | 'DRAFT' | 'SAVED';

const toYmd = (date: Date | null | undefined): string | undefined => {
  if (!date || Number.isNaN(date.getTime())) return undefined;
  return date.toISOString().slice(0, 10);
};

const statusVariant = (status: string): 'default' | 'info' | 'success' | 'danger' | 'warning' => {
  if (status === 'TEMP') return 'warning';
  if (status === 'DRAFT') return 'default';
  return 'success';
};

const formatJournalStatus = (status: string) => {
  if (status === 'DRAFT') return 'Draft';
  if (status === 'TEMP') return 'Temp';
  if (status === 'SAVED') return 'Saved';
  return status;
};

const formatAmount = (value?: number | null) =>
  value != null
    ? value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '0.00';

const formatCompanyCodes = (row: AccDocument) => {
  if (row.companyCodes?.length) return row.companyCodes.join(', ');
  const fromLines = row.lines
    ?.map((line) => line.company?.code ?? line.company?.name)
    .filter((value): value is string => !!value);
  if (!fromLines?.length) return '';
  return Array.from(new Set(fromLines)).join(', ');
};

const isJournalBalanced = (row: AccDocument) => {
  if (row.lineTotals?.isBalanced != null) return row.lineTotals.isBalanced;
  const totals = row.lineTotals;
  if (!totals) return true;
  return (
    Math.abs(totals.debitUSD - totals.creditUSD) < 0.005
    && Math.abs(totals.debitLocal - totals.creditLocal) < 0.005
  );
};

const viewTabs: Array<{
  id: StatusTab;
  label: string;
  emptyTitle: string;
  emptyHint: string;
}> = [
  {
    id: 'all',
    label: 'All',
    emptyTitle: 'No journals yet',
    emptyHint: 'Create a journal entry to post debits and credits.',
  },
  {
    id: 'DRAFT',
    label: 'Draft',
    emptyTitle: 'No draft journals',
    emptyHint: 'Draft journals you create or import will show up here.',
  },
  {
    id: 'SAVED',
    label: 'Saved',
    emptyTitle: 'No saved journals',
    emptyHint: 'Posted journals for the selected year appear here.',
  },
];

export const List: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnState = accountingReturnState(location.pathname, location.search);
  const confirm = useConfirm();

  const permissionKey = moduleConfig.module.permissionModuleKey ?? 'acc_documents';
  const canDelete = can(permissionKey, 'delete');
  const canEdit = can(permissionKey, 'edit');

  const [view, setView] = useState<StatusTab>('all');
  const [page, setPage] = useState(1);
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [year, setYear] = useState<string>('');
  const [datePreset, setDatePreset] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState<string | undefined>();
  const [dateTo, setDateTo] = useState<string | undefined>();

  const { data: yearBounds } = useSWR<{ minYear: number; maxYear: number }>(
    '/acc-documents/filter-years',
    fetcher,
  );

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const minYear = yearBounds?.minYear ?? currentYear;
    const maxYear = yearBounds?.maxYear ?? currentYear + 5;
    return buildYearFilterOptions(minYear, maxYear);
  }, [yearBounds]);

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, view, year, dateFrom, dateTo, itemsPerPage]);

  const { sortConfig, handleSort } = useGeneralListSort(
    { key: 'postingDate', direction: 'desc' },
    {
      defaultDirectionForKey: (key) =>
        key === 'code' || key === 'description' || key === 'documentType' || key === 'status'
          ? 'asc'
          : 'desc',
    },
  );

  useEffect(() => {
    setPage(1);
  }, [sortConfig.key, sortConfig.direction]);

  const listOptions = useMemo(() => {
    const filters: Record<string, string[]> = {};
    if (view === 'DRAFT') filters.status = ['DRAFT', 'TEMP'];
    else if (view === 'SAVED') filters.status = ['SAVED'];
    if (year) filters.year = [year];
    const dateRanges =
      dateFrom || dateTo
        ? {
            postingDate: {
              start: dateFrom ?? null,
              end: dateTo ?? null,
            },
          }
        : undefined;
    const hasListFilters = Object.keys(filters).length > 0 || Boolean(dateRanges);
    return {
      sortKey: sortConfig.key,
      sortDirection: sortConfig.direction,
      filters: hasListFilters
        ? {
            filters,
            ...(dateRanges ? { dateRanges } : {}),
          }
        : undefined,
    };
  }, [view, year, dateFrom, dateTo, sortConfig]);

  const { data, isLoading, total, mutate } = useAccDocuments(
    search || undefined,
    page,
    itemsPerPage,
    listOptions,
  );

  const totalPages = Math.max(1, Math.ceil((total ?? 0) / itemsPerPage));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const hasFilters = Boolean(search.trim() || year || datePreset || dateFrom || dateTo || view !== 'all');

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setYear('');
    setDatePreset(null);
    setDateFrom(undefined);
    setDateTo(undefined);
    setView('all');
  };

  const onDateFilterChange = (value: string | null, range?: DateRange) => {
    if (!value || value === 'all' || !range || (!range.start && !range.end)) {
      setDatePreset(null);
      setDateFrom(undefined);
      setDateTo(undefined);
      return;
    }
    setDatePreset(value);
    setDateFrom(toYmd(range.start));
    setDateTo(toYmd(range.end));
  };

  const handleDelete = async (e: React.MouseEvent, doc: AccDocument) => {
    e.stopPropagation();
    const label = doc.code || `Draft #${doc.id}`;
    const ok = await confirm({
      title: moduleConfig.delete.title,
      message: `Delete “${label}”?`,
      confirmText: moduleConfig.delete.confirmText,
      cancelText: moduleConfig.delete.cancelText,
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteAccDocument(doc.id);
      await mutate();
    } catch (err: any) {
      window.alert(err?.response?.data?.message || err?.message || moduleConfig.delete.errorMessage);
    }
  };

  const runStatusAction = async (
    e: React.MouseEvent,
    doc: AccDocument,
    action: 'save' | 'revert',
  ) => {
    e.stopPropagation();
    const label = doc.code || `Draft #${doc.id}`;
    const ok = await confirm(
      action === 'save'
        ? {
            title: 'Save journal',
            message: `Save “${label}”? Saved journals can no longer be edited until reverted to draft.`,
            confirmText: 'Save',
            cancelText: 'Cancel',
          }
        : {
            title: 'Revert to draft',
            message: `Revert “${label}” to draft? You will be able to edit it again.`,
            confirmText: 'Revert',
            cancelText: 'Cancel',
            confirmVariant: 'danger',
          },
    );
    if (!ok) return;
    try {
      if (action === 'save') await saveAccDocument(doc.id);
      else await revertAccDocumentToDraft(doc.id);
      toast.success(action === 'save' ? 'Journal saved' : 'Journal reverted to draft');
      await mutate();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      toast.error(msg || 'Action failed');
    }
  };

  const activeTab = useMemo(() => viewTabs.find((t) => t.id === view) ?? viewTabs[0], [view]);
  const formatDate = moduleConfig.utils.formatDate;

  return (
    <GeneralList
      tabs={viewTabs.map(({ id, label }) => ({ id, label }))}
      activeTab={view}
      onTabChange={setView}
      sorting={{ sortConfig, onSort: handleSort }}
      tabTrailing={
        <>
          <span
            className={cn('mx-1 my-1.5 w-px shrink-0 self-stretch', isDark ? 'bg-white/15' : 'bg-slate-300/80')}
            aria-hidden
          />
          <label
            className={cn(
              'relative inline-flex items-center rounded-md transition',
              year
                ? cn(colors.surface, 'shadow-sm ring-1', isDark ? 'ring-white/10' : 'ring-black/5')
                : colors.interactiveHover,
            )}
          >
            <span className="sr-only">Filter by year</span>
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className={cn(
                'appearance-none cursor-pointer rounded-md border-0 bg-transparent py-2 pl-3.5 pr-8 text-sm font-medium outline-none',
                'focus:ring-2 focus:ring-indigo-500/25',
                year ? colors.text : colors.textMuted,
              )}
              title="Filter by year"
            >
              <option value="">All years</option>
              {yearOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDownIcon
              className={cn(
                'pointer-events-none absolute right-2 h-3.5 w-3.5',
                year ? colors.primaryFg : 'opacity-50',
              )}
            />
          </label>
          <div className="relative z-30 shrink-0 self-center">
            <DateFilterDropdown
              label=""
              selectedValue={datePreset}
              placeholder="Posting date"
              onFilterChange={onDateFilterChange}
              variant="inline"
            />
          </div>
        </>
      }
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: moduleConfig.search.placeholder,
        title: 'Searches journal code and description',
      }}
      actions={
        <Link
          to={moduleConfig.module.route.new}
          state={returnState}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-medium text-white shadow-sm',
            colors.primary,
          )}
        >
          <PlusIcon className="h-4 w-4" />
          New journal
        </Link>
      }
      hasFilters={hasFilters}
      onClearFilters={clearFilters}
      isLoading={isLoading}
      loadingLabel="Loading journals…"
      isEmpty={data.length === 0}
      emptyState={{
        icon: <DocumentTextIcon className="h-12 w-12" />,
        title: hasFilters ? 'No matching journals' : activeTab.emptyTitle,
        hint: hasFilters ? 'Try adjusting filters or clearing the search.' : activeTab.emptyHint,
        action:
          hasFilters ? (
            <button
              type="button"
              onClick={clearFilters}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium',
                colors.border,
                colors.text,
              )}
            >
              Clear filters
            </button>
          ) : (
            <Link
              to={moduleConfig.module.route.new}
              state={returnState}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-white',
                colors.primary,
              )}
            >
              <PlusIcon className="h-4 w-4" />
              New journal
            </Link>
          ),
      }}
      pagination={{
        page,
        totalPages,
        total: total ?? 0,
        itemsPerPage,
        pageSizeOptions,
        onPageChange: setPage,
        onItemsPerPageChange: (size) => {
          setItemsPerPage(size);
          setPage(1);
        },
      }}
    >
      <table className="w-full table-fixed text-sm">
        <thead>
          <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
            <th className={cn('sticky top-0 z-10 w-32 font-medium', colors.surface)} />
            <GeneralListSortableTh sortKey="code" className="w-24">Ref</GeneralListSortableTh>
            <GeneralListSortableTh sortKey="documentDate" className="hidden w-28 md:table-cell">Doc. date</GeneralListSortableTh>
            <GeneralListSortableTh sortKey="description">Description</GeneralListSortableTh>
            <GeneralListSortableTh sortKey="postingDate" className="hidden w-28 md:table-cell">Posting</GeneralListSortableTh>
            <GeneralListSortableTh sortKey="documentType" className="hidden w-32 lg:table-cell">Type</GeneralListSortableTh>
            <GeneralListSortableTh sortKey="status" className="w-28">Status</GeneralListSortableTh>
            <th className={cn('sticky top-0 z-10 hidden w-24 font-medium text-right xl:table-cell', colors.surface, colors.textMuted)}>Debit USD</th>
            <th className={cn('sticky top-0 z-10 hidden w-24 font-medium text-right xl:table-cell', colors.surface, colors.textMuted)}>Credit USD</th>
            <th className={cn('sticky top-0 z-10 hidden w-28 font-medium lg:table-cell', colors.surface, colors.textMuted)}>Companies</th>
            <GeneralListSortableTh sortKey="attachments" className="hidden w-20 lg:table-cell" align="center">Files</GeneralListSortableTh>
            <th className={cn('sticky top-0 z-10 hidden w-20 font-medium sm:table-cell', colors.surface, colors.textMuted)}>Balanced</th>
          </tr>
        </thead>
        <tbody>
          {data.map((doc) => {
            const canSave = doc.status === 'DRAFT' || doc.status === 'TEMP';
            const canRevert = doc.status === 'SAVED';
            const editHref = resolveDocumentEditRoute(
              doc.documentType,
              doc.id,
              moduleConfig.module.route.edit(doc.id),
            );
            return (
              <tr
                key={doc.id}
                onClick={() => navigate(moduleConfig.module.route.detail(doc.id), { state: returnState })}
                className={cn(
                  'group cursor-pointer border-b transition last:border-b-0',
                  colors.border,
                  colors.interactiveHover,
                )}
              >
                <td className="w-32">
                  <div className="flex items-center flex-nowrap">
                    <Link
                      onClick={(e) => e.stopPropagation()}
                      to={moduleConfig.module.route.detail(doc.id)}
                      state={returnState}
                      title="Open"
                      aria-label="Open journal"
                      className={cn('rounded-lg p-1 transition-colors', colors.primaryFg, colors.interactiveHover)}
                    >
                      <EyeIcon className="w-5 h-5" />
                    </Link>
                    {canSave && (
                      <button
                        type="button"
                        title="Save"
                        aria-label="Save journal"
                        onClick={(e) => runStatusAction(e, doc, 'save')}
                        className={cn(
                          'rounded-lg p-1 transition-colors',
                          isDark ? 'text-emerald-400' : 'text-emerald-600',
                          colors.interactiveHover,
                        )}
                      >
                        <CheckCircleIcon className="w-5 h-5" />
                      </button>
                    )}
                    {canRevert && (
                      <button
                        type="button"
                        title="Revert to draft"
                        aria-label="Revert to draft"
                        onClick={(e) => runStatusAction(e, doc, 'revert')}
                        className={cn('rounded-lg p-1 transition-colors', colors.textMuted, colors.interactiveHover)}
                      >
                        <UndoIcon className="w-5 h-5" />
                      </button>
                    )}
                    {canEdit && canSave && (
                      <Link
                        onClick={(e) => e.stopPropagation()}
                        to={editHref}
                        state={returnState}
                        title="Edit"
                        aria-label="Edit journal"
                        className={cn('rounded-lg p-1 transition-colors', colors.primaryFg, colors.interactiveHover)}
                      >
                        <PencilIcon className="w-5 h-5" />
                      </Link>
                    )}
                    {canDelete && canSave && (
                      <button
                        type="button"
                        title="Delete"
                        aria-label="Delete journal"
                        onClick={(e) => handleDelete(e, doc)}
                        className={cn(
                          'rounded-lg p-1 transition-colors',
                          isDark ? 'text-red-400' : 'text-red-600',
                          colors.interactiveHover,
                        )}
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </td>
                <td className="w-24">
                  <div className="min-w-0">
                    <p className={cn('truncate font-medium tabular-nums', colors.text)}>
                      {doc.code || `#${doc.id}`}
                    </p>
                    <p className={cn('mt-1 truncate text-xs md:hidden', colors.textMuted)}>
                      {formatDate(doc.documentDate ?? undefined)}
                      {doc.postingDate ? ` · ${formatDate(doc.postingDate)}` : ''}
                      {doc.documentType?.code ? ` · ${doc.documentType.code}` : ''}
                    </p>
                  </div>
                </td>
                <td className={cn('hidden whitespace-nowrap md:table-cell', colors.textMuted)}>
                  {formatDate(doc.documentDate ?? undefined)}
                </td>
                <td className="max-w-0">
                  {doc.description?.trim() ? (
                    <div className={cn('text-sm', colors.text)}>
                      <TruncatedText text={doc.description.trim()} maxLength={100} className="text-sm" />
                    </div>
                  ) : (
                    <span className={colors.textMuted}></span>
                  )}
                </td>
                <td className={cn('hidden whitespace-nowrap md:table-cell', colors.textMuted)}>
                  {formatDate(doc.postingDate ?? undefined)}
                </td>
                <td className={cn('hidden truncate lg:table-cell', colors.text)}>
                  {doc.documentType?.name ?? doc.documentType?.code ?? ''}
                </td>
                <td className="whitespace-nowrap">
                  <span className="inline-flex items-center gap-1.5">
                    <Badge variant={statusVariant(doc.status)} className="whitespace-nowrap">
                      {formatJournalStatus(doc.status)}
                    </Badge>
                    {doc.periodLocked && (
                      <span
                        title="Accounting period is locked"
                        className={cn('inline-flex', isDark ? 'text-amber-400' : 'text-amber-600')}
                      >
                        <LockIcon className="h-4 w-4" />
                      </span>
                    )}
                  </span>
                </td>
                <td className={cn('hidden whitespace-nowrap text-right tabular-nums xl:table-cell', colors.textMuted)}>
                  {formatAmount(doc.lineTotals?.debitUSD)}
                </td>
                <td className={cn('hidden whitespace-nowrap text-right tabular-nums xl:table-cell', colors.textMuted)}>
                  {formatAmount(doc.lineTotals?.creditUSD)}
                </td>
                <td className={cn('hidden truncate lg:table-cell', colors.textMuted)}>
                  {formatCompanyCodes(doc)}
                </td>
                <td className={cn('hidden whitespace-nowrap text-center tabular-nums lg:table-cell', colors.textMuted)}>
                  {(() => {
                    const count = doc._count?.attachments ?? doc.attachments?.length ?? 0;
                    return count > 0 ? count : null;
                  })()}
                </td>
                <td className="hidden sm:table-cell">
                  <Badge variant={isJournalBalanced(doc) ? 'success' : 'danger'}>
                    {isJournalBalanced(doc) ? 'Yes' : 'No'}
                  </Badge>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </GeneralList>
  );
};
